// src/services/indexedDB/repositories/taskRepository.ts
// Task 專屬邏輯

import { Task } from '../../../types/task';
import { BaseRepository } from '../core/base-repository';
import { STORE_NAMES, initDB } from '../core/database';
import { CachedEntity } from '../core/types';
import { logError } from '../../../utils/logger';
import { formatDateToString } from '../../../utils/dateUtils';

// IndexedDB 序列化後的 Task（日期欄位為 number timestamp）
type StoredTask = Omit<CachedEntity<Task>, 'createdAt' | 'updatedAt' | 'closedAt'> & {
  createdAt: number;
  updatedAt?: number;
  closedAt?: number;
};

/**
 * Task Repository
 * 處理 Task 相關的 IndexedDB 操作
 */
class TaskRepository extends BaseRepository<Task> {
  protected readonly storeName = STORE_NAMES.TASKS;

  /**
   * 準備 Task 以儲存
   */
  protected prepareForStorage(entity: Task): CachedEntity<Task> {
    const prepared = super.prepareForStorage(entity);

    // 序列化額外的日期欄位
    if (entity.closedAt) {
      (prepared as unknown as StoredTask).closedAt = entity.closedAt.getTime();
    }

    return prepared;
  }

  /**
   * 反序列化 Task
   */
  protected deserialize(stored: CachedEntity<Task>): Task {
    const base = super.deserialize(stored);

    // 轉換 closedAt
    const storedClosedAt = (stored as unknown as StoredTask).closedAt;
    if (storedClosedAt) {
      return {
        ...base,
        closedAt: typeof storedClosedAt === 'number' ? new Date(storedClosedAt) : storedClosedAt,
      };
    }

    return base;
  }

  /**
   * 獲取開放的任務
   */
  async getOpen(userId: string, limit: number = 20): Promise<Task[]> {
    try {
      const db = await initDB();
      const tx = db.transaction(this.storeName, 'readonly');
      const store = tx.objectStore(this.storeName);
      const index = store.index('userId_status');

      return new Promise((resolve, reject) => {
        const request = index.getAll([userId, 'OPEN']);

        request.onsuccess = () => {
          let results = request.result.map((item: CachedEntity<Task>) => this.deserialize(item));

          // 應用 limit
          if (limit) {
            results = results.slice(0, limit);
          }

          resolve(results);
        };

        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      logError('Failed to get open tasks', error);
      throw error;
    }
  }

  /**
   * 刪除指定用戶特定狀態的所有任務
   * 用於清除 IndexedDB 中殘留的 CLOSED 任務快取
   */
  async deleteByStatus(userId: string, status: string): Promise<void> {
    try {
      const db = await initDB();
      const tx = db.transaction(this.storeName, 'readwrite');
      const store = tx.objectStore(this.storeName);
      const index = store.index('userId_status');

      return new Promise((resolve, reject) => {
        const request = index.getAll([userId, status]);

        request.onsuccess = () => {
          for (const item of request.result) {
            store.delete(item.id);
          }
          tx.oncomplete = () => resolve();
          tx.onerror = () => reject(tx.error);
        };

        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      logError('Failed to delete tasks by status', error);
      throw error;
    }
  }

  /**
   * 限制 CLOSED 任務快取數量，保留最新的 maxCount 筆
   * 防止長期使用後 CLOSED task 無限累積導致 IndexedDB I/O 過載
   */
  async pruneClosedTasks(userId: string, maxCount: number): Promise<void> {
    try {
      const db = await initDB();
      const tx = db.transaction(this.storeName, 'readwrite');
      const store = tx.objectStore(this.storeName);
      const index = store.index('userId_status');

      return new Promise((resolve, reject) => {
        const request = index.getAll([userId, 'CLOSED']);

        request.onsuccess = () => {
          const closedTasks: CachedEntity<Task>[] = request.result;

          if (closedTasks.length <= maxCount) {
            resolve();
            return;
          }

          // 按 closedAt（或 updatedAt）由新到舊排序，保留最新的 maxCount 筆
          closedTasks.sort((a, b) => {
            const sa = a as unknown as StoredTask;
            const sb = b as unknown as StoredTask;
            const aTime = sa.closedAt ?? sa.updatedAt ?? sa.createdAt ?? 0;
            const bTime = sb.closedAt ?? sb.updatedAt ?? sb.createdAt ?? 0;
            return bTime - aTime;
          });

          // 刪除超出上限的舊任務
          const toDelete = closedTasks.slice(maxCount);
          for (const item of toDelete) {
            store.delete(item.id);
          }

          tx.oncomplete = () => resolve();
          tx.onerror = () => reject(tx.error);
        };

        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      logError('Failed to prune closed tasks', error);
      throw error;
    }
  }

  /**
   * 取得今日 closed 的 task 數量（供 Evening 摘要使用）
   * 從 IndexedDB 直接讀取，不需要 Firestore subscription
   *
   * 注意：IndexedDB 的 `userId_status` index 只能篩選到 [userId, 'CLOSED']，
   * 無法在 index 層進一步限制 closedAt 日期，因此先取出所有 CLOSED tasks，
   * 再於 JS 層過濾 closedAt 日期。若 CLOSED tasks 量極大，需改用 cursor 逐筆讀取。
   *
   * @param userId - 用戶 ID
   * @param todayDateStr - YYYY-MM-DD 格式的今日日期字串
   */
  async countClosedToday(userId: string, todayDateStr: string): Promise<number> {
    try {
      const db = await initDB();
      const tx = db.transaction(this.storeName, 'readonly');
      const store = tx.objectStore(this.storeName);
      const index = store.index('userId_status');

      return new Promise((resolve, reject) => {
        const request = index.getAll([userId, 'CLOSED']);

        request.onsuccess = () => {
          const count = request.result.filter((item: CachedEntity<Task>) => {
            const stored = item as unknown as StoredTask;
            if (!stored.closedAt) return false;
            return formatDateToString(new Date(stored.closedAt)) === todayDateStr;
          }).length;
          resolve(count);
        };

        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      logError('Failed to count closed tasks today', error);
      return 0;
    }
  }

  /**
   * 回滾任務到原始狀態
   * 用於樂觀更新失敗後的回滾
   */
  async revert(taskId: string, originalTask: Task): Promise<void> {
    if (!taskId || !originalTask) {
      throw new Error('Invalid parameters provided to revertTask');
    }

    try {
      const db = await initDB();
      const tx = db.transaction(this.storeName, 'readwrite');
      const store = tx.objectStore(this.storeName);

      const prepared = this.prepareForStorage(originalTask);

      return new Promise((resolve, reject) => {
        const request = store.put(prepared);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      logError('Failed to revert task', error);
      throw error;
    }
  }
}

// 導出單例
export const taskRepository = new TaskRepository();
